import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api/api';

const CriarAcordo = () => {
    const { dividaId } = useParams(); // Recebe o ID da dívida pela URL
    const navigate = useNavigate();

    const [divida, setDivida] = useState(null);
    const [loading, setLoading] = useState(true);
    const [gerando, setGerando] = useState(false);
    
    // Dados da Proposta
    const [dados, setDados] = useState({
        valorAcordado: '',
        parcelas: 1
    });

    const [linkGerado, setLinkGerado] = useState('');

    useEffect(() => {
        // 🛡️ TRAVA DE SEGURANÇA: Evita Erro 403 no backend
        if (!dividaId || dividaId === 'undefined') {
            setLoading(false);
            return; 
        }

        // Busca os dados da dívida para mostrar o saldo atualizado
        api.get(`/assinante/dividas/${dividaId}`)
            .then(res => {
                setDivida(res.data);
                // Sugere o valor atualizado como valor inicial do acordo
                setDados(prev => ({ ...prev, valorAcordado: res.data.valorAtualizado }));
                setLoading(false);
            })
            .catch(err => {
                console.error(err);
                alert("Erro ao carregar dados da dívida.");
                navigate('/dashboard');
            });
    }, [dividaId, navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setDados(prev => ({ ...prev, [name]: value }));
    };

    const handleGerarAcordo = async (e) => {
        e.preventDefault();
        setGerando(true);

        try {
            const res = await api.post('/assinante/acordos', {
                dividaId: dividaId,
                valorAcordado: parseFloat(dados.valorAcordado),
                parcelas: parseInt(dados.parcelas)
            });

            // ✅ CORREÇÃO: Pegar o token seguro (UUID) em vez do ID do banco
            const tokenAcesso = res.data.tokenAcesso;
            
            // Monta o link que o devedor vai aceder (Página Pública)
            const urlPublica = `${window.location.origin}/acordo/${tokenAcesso}`;
            setLinkGerado(urlPublica);

        } catch (error) {
            console.error(error);
            alert("Erro ao gerar proposta de acordo.");
        } finally {
            setGerando(false);
        }
    };

    const copiarLink = () => {
        navigator.clipboard.writeText(linkGerado);
        alert("Link copiado! Agora é só enviar no WhatsApp.");
    };

    if (loading) return <div style={styles.container}><p style={{color: 'white'}}>A carregar dados...</p></div>;

    // Se o loading terminou e não encontrou dívida (caiu na trava de segurança)
    if (!divida) {
        return (
            <div style={styles.container}>
                <div style={styles.card}>
                    <h2 style={styles.title}>Nenhuma Dívida Selecionada</h2>
                    <p style={{color: '#94a3b8'}}>Acede ao perfil do devedor e clica em "Gerar Acordo" na dívida específica.</p>
                    <button onClick={() => navigate('/dashboard')} style={styles.btnVoltar}>← Voltar ao Início</button>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.content}>
                <button onClick={() => navigate('/dashboard')} style={styles.btnVoltar}>
                    ← Voltar
                </button>

                <div style={styles.card}>
                    <h2 style={styles.title}>Gerar Acordo</h2>
                    
                    {/* RESUMO DA DÍVIDA ATUAL */}
                    <div style={styles.infoBox}>
                        <p><strong>Devedor:</strong> {divida.devedorNome}</p>
                        <p><strong>Dívida Original:</strong> R$ {divida.valorOriginal.toFixed(2)}</p>
                        <p style={{color: '#10b981'}}><strong>Saldo Atualizado:</strong> R$ {divida.valorAtualizado.toFixed(2)}</p>
                    </div>

                    {!linkGerado ? (
                        <form onSubmit={handleGerarAcordo} style={styles.form}>
                            <div style={styles.inputGroup}>
                                <label style={styles.label}>Valor Total do Acordo (R$)</label>
                                <input 
                                    type="number" 
                                    name="valorAcordado"
                                    step="0.01"
                                    value={dados.valorAcordado}
                                    onChange={handleChange}
                                    style={styles.input}
                                    required
                                />
                                <small style={{color: '#94a3b8'}}>Podes dar um desconto sobre o valor atualizado.</small>
                            </div>

                            <div style={styles.inputGroup}>
                                <label style={styles.label}>Quantidade de Parcelas</label>
                                <select 
                                    name="parcelas" 
                                    value={dados.parcelas} 
                                    onChange={handleChange} 
                                    style={styles.input}
                                >
                                    {[1, 2, 3, 4, 5, 6, 10, 12].map(n => (
                                        <option key={n} value={n}>{n === 1 ? 'À vista' : `${n} parcelas`}</option>
                                    ))}
                                </select>
                            </div>

                            {dados.parcelas > 1 && (
                                <p style={styles.preview}>
                                    Valor da Parcela: <strong>R$ {(dados.valorAcordado / dados.parcelas).toFixed(2)}</strong>
                                </p>
                            )}

                            <button type="submit" disabled={gerando} style={styles.btnGerar}>
                                {gerando ? 'A processar...' : 'Gerar Link de Assinatura'}
                            </button>
                        </form>
                    ) : (
                        // TELA APÓS GERAR O LINK
                        <div style={styles.successBox}>
                            <p style={{color: '#10b981', fontWeight: 'bold'}}>✓ Proposta de Acordo Criada!</p>
                            <p style={{color: '#94a3b8', fontSize: '0.9rem'}}>Envia o link abaixo para o devedor assinar:</p>
                            
                            <div style={styles.linkWrapper}>
                                <input readOnly value={linkGerado} style={styles.inputLink} />
                                <button onClick={copiarLink} style={styles.btnCopiar}>Copiar</button>
                            </div>

                            <button 
                                onClick={() => window.open(`https://wa.me/${divida.devedorTelefone}?text=Olá, ${divida.devedorNome}. Geramos uma proposta de acordo para a sua pendência. Aceda ao link para conferir e assinar digitalmente: ${linkGerado}`, '_blank')}
                                style={styles.btnWhatsapp}
                            >
                                Enviar via WhatsApp
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const styles = {
    container: { minHeight: '100vh', background: '#020617', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' },
    content: { width: '100%', maxWidth: '500px' },
    btnVoltar: { background: 'transparent', color: '#94a3b8', border: 'none', cursor: 'pointer', marginBottom: '15px' },
    card: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', padding: '30px', borderRadius: '16px', boxSizing: 'border-box' },
    title: { color: 'white', marginTop: 0, marginBottom: '20px' },
    infoBox: { background: 'rgba(0,0,0,0.2)', padding: '15px', borderRadius: '8px', marginBottom: '25px', color: '#f8fafc' },
    form: { display: 'flex', flexDirection: 'column', gap: '20px' },
    inputGroup: { display: 'flex', flexDirection: 'column', gap: '8px' },
    label: { color: '#f8fafc', fontSize: '0.9rem', fontWeight: 'bold' },
    input: { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', padding: '12px', borderRadius: '8px', color: 'white', fontSize: '1rem' },
    preview: { color: '#10b981', textAlign: 'center', margin: '10px 0' },
    btnGerar: { background: '#10b981', color: '#022c22', padding: '15px', borderRadius: '8px', border: 'none', fontWeight: 'bold', cursor: 'pointer' },
    
    // Estilos do Sucesso
    successBox: { textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '15px' },
    linkWrapper: { display: 'flex', gap: '5px' },
    inputLink: { flex: 1, background: '#0f172a', border: '1px solid #1e293b', color: '#94a3b8', padding: '10px', borderRadius: '4px', fontSize: '0.8rem' },
    btnCopiar: { background: '#334155', color: 'white', border: 'none', padding: '0 15px', borderRadius: '4px', cursor: 'pointer' },
    btnWhatsapp: { background: '#25D366', color: 'white', padding: '15px', borderRadius: '8px', border: 'none', fontWeight: 'bold', cursor: 'pointer', marginTop: '10px' }
};

export default CriarAcordo;